// apps/storefront/src/lib/privacyDecision.ts
//
// The one place that decides whether a visitor may be measured or marketed to.
// Ported from WOWsite (`packages/shared-utils/src/privacyDecision.ts`, 2026-10)
// with two deliberate differences: SiFly has two modes, not four (no
// jurisdiction lookup, no collect-nothing mode — the mode is derived from the
// existing `storefront_settings.cookie_consent_enabled` flag), and a visitor
// who sends GPC can still opt in themselves from the footer panel
// (`gpcOverride`), where WOWsite treats GPC as final.
//
// Pure: no DOM, no fetch, no storage. The browser half (privacyRuntime.ts) and
// the server half (privacyServer.ts) both call `isAllowed`, so the two can
// never disagree about the rules — only about their inputs.

export type Purpose = 'analytics' | 'measurement' | 'advertising' | 'session_replay'
export type PrivacyMode = 'us_opt_out' | 'consent_required'
export type PurposeDecision = 'allow' | 'deny'
export type PurposeGroup = 'site_analytics' | 'marketing'

// ORDER IS THE COOKIE FORMAT (encodePreference): append new purposes, never reorder.
export const PURPOSES = ['analytics', 'measurement', 'advertising', 'session_replay'] as const satisfies readonly Purpose[]

/**
 * What each purpose covers:
 *   analytics      — SiFly's own first-party events (`track()` → analytics_events)
 *   measurement    — Google Analytics 4
 *   advertising    — Meta Pixel, and any first-party marketing cookie
 *                    (the partner attribution cookie, `sf_partner`)
 *   session_replay — Microsoft Clarity
 *
 * The visitor sees two switches, one per group. The engine stays per-purpose
 * so a vendor added later lands in a group instead of growing the UI.
 */
export const PURPOSE_GROUPS: Record<PurposeGroup, readonly Purpose[]> = {
  site_analytics: ['analytics', 'measurement'],
  marketing: ['advertising', 'session_replay'],
}

/** A vendor this tenant has actually configured. Feeds the notice version. */
export type NoticeVendor = 'measurement' | 'advertising' | 'session_replay'

function groupForPurpose(purpose: Purpose): PurposeGroup {
  return PURPOSE_GROUPS.site_analytics.includes(purpose) ? 'site_analytics' : 'marketing'
}

/**
 * The banner flag is the mode. Banner on → nothing runs without an explicit
 * allow. Banner off → notice-and-opt-out: collect by default, stop when the
 * visitor says so in the footer panel (WOWsite's default posture).
 */
export function privacyModeFor(cookieConsentEnabled: boolean | null | undefined): PrivacyMode {
  return cookieConsentEnabled === true ? 'consent_required' : 'us_opt_out'
}

export type SavedPreference = {
  version: 2
  purposes: Partial<Record<Purpose, PurposeDecision>>
  updatedAt: number
  /** The site's notice version when the choice was made (`noticeVersionFor`). */
  noticeVersion: string
  /**
   * True when the choice was saved while the browser was sending GPC. Only such
   * a choice can grant anything to a GPC visitor: an allow given before GPC was
   * switched on is overridden by the signal, an allow given despite it is the
   * visitor's own later word.
   */
  gpcOverride: boolean
}

/**
 * Identifies the notice a choice was given against. Derived from what the site
 * runs, so it can never drift from a hand-maintained string: adding a vendor
 * changes it, and a consent-mode visitor who agreed before that vendor existed
 * is asked again. djb2, same as WOWsite.
 */
export function noticeVersionFor({ mode, vendors }: { mode: PrivacyMode; vendors: readonly NoticeVendor[] }): string {
  const s = `${mode}|${[...vendors].sort().join(',')}`
  let h = 5381
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0
  return `n${(h >>> 0).toString(36)}`
}

/**
 * The visitor's answer for one purpose: their own entry, otherwise the most
 * restrictive answer they gave for a sibling in the same group.
 */
export function effectivePurposeDecision(purpose: Purpose, saved: SavedPreference | null): PurposeDecision | undefined {
  if (!saved) return undefined
  const own = saved.purposes[purpose]
  if (own) return own
  const siblings = PURPOSE_GROUPS[groupForPurpose(purpose)]
    .map((p) => saved.purposes[p])
    .filter((d): d is PurposeDecision => d !== undefined)
  if (siblings.length === 0) return undefined
  return siblings.includes('deny') ? 'deny' : 'allow'
}

export type DecideInput = {
  purpose: Purpose
  mode: PrivacyMode
  saved: SavedPreference | null
  /** `navigator.globalPrivacyControl === true` in the browser, `Sec-GPC: 1` on the server. */
  gpc: boolean
  /** The CURRENT notice version, to compare against the saved one. */
  noticeVersion: string
}

export function isAllowed({ purpose, mode, saved, gpc, noticeVersion }: DecideInput): boolean {
  const decision = effectivePurposeDecision(purpose, saved)
  const current = saved !== null && saved.noticeVersion === noticeVersion

  // 1. GPC. Our policy reads it as "do not measure or market at all" —
  //    stricter than the signal's own sale/sharing scope — and it outranks a
  //    saved allow, EXCEPT one the visitor made while sending it (the footer
  //    panel). The banner never asks a GPC visitor; only they can start this.
  if (gpc) return decision === 'allow' && current && saved?.gpcOverride === true

  // 2. An explicit deny is honoured whatever the notice version says.
  if (decision === 'deny') return false

  // 3. An allow counts only while the notice it was given against still
  //    describes the site. A changed vendor set re-asks in consent mode.
  if (decision === 'allow' && current) return true

  // 4. Consent mode: nothing runs without an explicit, current allow.
  if (mode === 'consent_required') return false

  // 5. Notice-and-opt-out: collect until the visitor says otherwise.
  return true
}

// ── Cookie encoding ─────────────────────────────────────────────────────────
//
// The choice lives in a first-party cookie (not localStorage) so a route
// handler can read it — the partner attribution redirect has to decide
// server-side whether it may set its own cookie. A record of the visitor's
// privacy choice is strictly necessary, so this cookie needs no consent itself.
//
// Format: `2.<a|d|- per PURPOSES>.<updatedAt base36>.<noticeVersion>.<0|1 gpcOverride>`
// e.g. `2.aadd.mfz3k1x0.n1a2b3c.0` — cookie-safe characters only.

export const PRIVACY_COOKIE = 'sf_privacy'
export const PREFERENCE_TTL_DAYS = 365

export function isExpired(updatedAt: number, now: number = Date.now()): boolean {
  return now - updatedAt > PREFERENCE_TTL_DAYS * 86_400_000
}

export function encodePreference(p: SavedPreference): string {
  const decisions = PURPOSES.map((k) => (p.purposes[k] === 'allow' ? 'a' : p.purposes[k] === 'deny' ? 'd' : '-')).join('')
  return `2.${decisions}.${p.updatedAt.toString(36)}.${p.noticeVersion}.${p.gpcOverride ? 1 : 0}`
}

/** Malformed, unknown-version or expired → null (fail closed: no stored choice). */
export function parsePreference(raw: string | null | undefined, now: number = Date.now()): SavedPreference | null {
  if (!raw) return null
  const parts = raw.split('.')
  if (parts.length !== 5 || parts[0] !== '2') return null
  const [, decisions, ts, noticeVersion, gpc] = parts
  // A SHORTER decision string is a cookie written before a purpose was added:
  // the missing purposes read as unanswered (and fall back to their group's
  // answer), so an opt-out survives the addition. Rejecting it would read as
  // "no choice" — i.e. collect, in opt-out mode. New purposes go at the END.
  if (decisions.length === 0 || decisions.length > PURPOSES.length || !/^[ad-]+$/.test(decisions)) return null
  if (!/^[0-9a-z]+$/.test(ts) || !/^(n[0-9a-z]+)?$/.test(noticeVersion) || (gpc !== '0' && gpc !== '1')) return null
  const updatedAt = parseInt(ts, 36)
  if (!Number.isFinite(updatedAt) || isExpired(updatedAt, now)) return null
  const purposes: Partial<Record<Purpose, PurposeDecision>> = {}
  PURPOSES.forEach((k, i) => {
    // decisions[i] is undefined past the end of an older, shorter cookie.
    if (decisions[i] === 'a') purposes[k] = 'allow'
    else if (decisions[i] === 'd') purposes[k] = 'deny'
  })
  return { version: 2, purposes, updatedAt, noticeVersion, gpcOverride: gpc === '1' }
}

/** One cookie's value out of a `Cookie` header / `document.cookie` string. */
export function readCookieValue(cookieString: string | null | undefined, name: string): string | null {
  if (!cookieString) return null
  for (const part of cookieString.split(';')) {
    const eq = part.indexOf('=')
    if (eq === -1) continue
    if (part.slice(0, eq).trim() === name) return part.slice(eq + 1).trim()
  }
  return null
}
